import { Injectable } from '@angular/core';
import { extractDepartmentId } from '@features/shared/departments/domain/utils/extract-department-id.util';
import { Client } from '../../domain/models/client.model';
import { ClientPaymentStatus } from '../../domain/models/client-payment-status.model';
import { ClientUpdateInput } from '../../domain/models/client-update-input.model';
import { PatientSummary } from '../../domain/models/patient-summary.model';
import { ClientDto } from '../dtos/client.dto';
import { PatientSummaryDto } from '../dtos/patient-summary.dto';
import { ClientMetrics } from '../../domain/models/client-metrics.model';
import { ClientMetricsDto } from '../dtos/client-metrics.dto';

function toPaymentStatus(value: string | null | undefined): ClientPaymentStatus {
  switch (value) {
    case 'up-to-date':
    case 'pending':
    case 'overdue':
      return value;
    default:
      return 'unknown';
  }
}

@Injectable()
export class ClientMapper {
  public toClient(dto: ClientDto): Client {
    return {
      id: dto.ExternalId,
      fullName: this.resolveFullName(dto),
      firstName: dto.FirstName ?? null,
      lastName: dto.LastName ?? null,
      email: dto.Email ?? '',
      phone: this.resolvePhone(dto.PhoneNumbers),
      petCount: (dto.Patients ?? []).length,
      patients: (dto.Patients ?? []).map((patient) => this.toPatientSummary(patient)),
      tags: (dto.Tags ?? []).filter((tag): tag is string => !!tag),
      status: dto.Archived ? 'archived' : dto.OnStop ? 'on-stop' : 'active',
      address: dto.StreetAddress ?? null,
      city: dto.City ?? null,
      zipCode: dto.ZipCode ?? null,
      clientSince: this.resolveClientSince(dto.Created),
      lastVisitDate: dto.LastConsultation ?? null,
      paymentStatus: toPaymentStatus(dto.PaymentStatus),
      pendingAmount: dto.PendingAmount ?? null,
      title: dto.Title ?? null,
      organizationName: dto.OrganizationName ?? null,
      vatNumber: dto.VatNumber ?? null,
      secondaryOwnerFirstName: dto.SecondaryOwnerFirstName ?? null,
      secondaryOwnerLastName: dto.SecondaryOwnerLastName ?? null,
      internalNotes: dto.CriticalNotes ?? null,
      remarks: dto.Remarks ?? null,
      noEmail: dto.NoEmail ?? false,
      noSms: dto.NoSms ?? false,
      homeDepartmentExternalId: extractDepartmentId(dto.HomeDepartmentUrl),
    };
  }

  public toClientMetrics(dto: ClientMetricsDto): ClientMetrics {
    return {
      newClientsLast30Days: dto.NewClientsLast30Days ?? 0,
      activeClients: dto.ActiveClients ?? 0,
      archivedClients: dto.ArchivedClients ?? 0,
    };
  }

  // Both POST and PATCH /api/clients now bind strongly-typed `CreateClientRequest`/
  // `UpdateClientRequest` (PascalCase, `PropertyNamingPolicy = null` -- see Program.cs), so both
  // payloads below use the exact PascalCase property names. Previously `toUpdatePayload` emitted
  // snake_case keys left over from when the PATCH endpoint took an untyped `JsonObject` -- those
  // keys don't bind to `UpdateClientRequest`'s properties, so every field it set (other than the
  // ones with no underscore, e.g. `email`) was silently dropped before ever reaching ProVet.
  public toUpdatePayload(changes: ClientUpdateInput): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    if (changes.firstName !== undefined) payload['FirstName'] = changes.firstName;
    if (changes.lastName !== undefined) payload['LastName'] = changes.lastName;
    if (changes.email !== undefined) payload['Email'] = changes.email;
    if (changes.phone !== undefined) payload['PhoneNumbers'] = [changes.phone];
    if (changes.address !== undefined) payload['StreetAddress'] = changes.address;
    if (changes.zipCode !== undefined) payload['ZipCode'] = changes.zipCode;
    if (changes.city !== undefined) payload['City'] = changes.city;
    if (changes.internalNotes !== undefined) payload['CriticalNotes'] = changes.internalNotes;
    if (changes.title !== undefined) payload['Title'] = changes.title;
    if (changes.organizationName !== undefined) payload['OrganizationName'] = changes.organizationName;
    if (changes.vatNumber !== undefined) payload['VatNumber'] = changes.vatNumber;
    if (changes.secondaryOwnerFirstName !== undefined) {
      payload['SecondaryOwnerFirstName'] = changes.secondaryOwnerFirstName;
    }
    if (changes.secondaryOwnerLastName !== undefined) {
      payload['SecondaryOwnerLastName'] = changes.secondaryOwnerLastName;
    }
    if (changes.remarks !== undefined) payload['Remarks'] = changes.remarks;
    if (changes.noEmail !== undefined) payload['NoEmail'] = changes.noEmail;
    if (changes.noSms !== undefined) payload['NoSms'] = changes.noSms;
    // A PRESENT array (including []) is a full tags replacement -- see `ClientUpdateInput.tags`.
    if (changes.tags !== undefined) payload['Tags'] = changes.tags;

    return payload;
  }

  public toCreatePayload(input: ClientUpdateInput): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      FirstName: input.firstName ?? '',
      LastName: input.lastName ?? '',
    };

    if (input.email) payload['Email'] = input.email;
    if (input.phone) payload['PhoneNumbers'] = [input.phone];
    if (input.address) payload['StreetAddress'] = input.address;
    if (input.zipCode) payload['ZipCode'] = input.zipCode;
    if (input.city) payload['City'] = input.city;
    if (input.internalNotes) payload['CriticalNotes'] = input.internalNotes;
    if (input.title) payload['Title'] = input.title;
    if (input.organizationName) payload['OrganizationName'] = input.organizationName;
    if (input.vatNumber) payload['VatNumber'] = input.vatNumber;
    if (input.secondaryOwnerFirstName) payload['SecondaryOwnerFirstName'] = input.secondaryOwnerFirstName;
    if (input.secondaryOwnerLastName) payload['SecondaryOwnerLastName'] = input.secondaryOwnerLastName;
    if (input.remarks) payload['Remarks'] = input.remarks;
    if (input.noEmail !== undefined) payload['NoEmail'] = input.noEmail;
    if (input.noSms !== undefined) payload['NoSms'] = input.noSms;
    if (input.tags !== undefined) payload['Tags'] = input.tags;

    return payload;
  }

  private resolveFullName(dto: ClientDto): string {
    const fullName = dto.FullName?.trim();
    if (fullName) return fullName;

    const combined = [dto.FirstName, dto.LastName].filter(Boolean).join(' ').trim();
    if (combined) return combined;

    return dto.Email ?? '';
  }

  private resolvePhone(entries: readonly unknown[]): string | null {
    const first = entries?.[0];
    if (first == null) return null;

    if (typeof first === 'string') {
      const trimmed = first.trim();
      return trimmed || null;
    }

    if (typeof first === 'object') {
      const record = first as Record<string, unknown>;
      for (const key of ['number', 'phone', 'value']) {
        const candidate = record[key];
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      }
    }

    return null;
  }

  private resolveClientSince(created: string | null | undefined): number | null {
    if (!created) return null;

    const parsed = new Date(created);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
  }

  private toPatientSummary(dto: PatientSummaryDto): PatientSummary {
    return {
      id: dto.ExternalId,
      externalClientId: dto.ExternalClientId,
      name: dto.Name,
      speciesId: dto.SpeciesId ?? null,
      breedId: dto.BreedId ?? null,
      genderId: dto.GenderId ?? null,
      gender: (dto.Gender ?? null) as 1 | 2 | null,
      neutered: dto.Neutered ?? null,
      archived: dto.Archived,
      deceased: dto.Deceased,
      weight: dto.Weight ?? null,
      dateOfBirth: dto.DateOfBirth ?? null,
      pictureThumb: dto.PictureThumb ?? null,
      pictureMedium: dto.PictureMedium ?? null,
      criticalNotes: dto.CriticalNotes ?? null,
      remarks: dto.Remarks ?? null,
      tags: (dto.Tags ?? []).filter((tag): tag is string => !!tag),
    };
  }
}
